// src/pages/ContactUs.js
import React, { useState, useEffect } from "react";
import "../styles/ContactUs.css";
import BackToTop from "../components/BackToTop";
import SEO from "../components/SEO";
import api, { API_ENDPOINTS } from "../config/api";

// Import images
import contactHero from "../assets/images/contact-hero.jpg";

const ContactUs = () => {
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await api.get(API_ENDPOINTS.PAGES.CONTACT);
      } catch (error) {
        console.error("Error fetching contact info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setSubmitStatus(null);

    try {
      const response = await api.post(API_ENDPOINTS.CONTACT.SUBMIT, formData);

      if (response.data.success) {
        setSubmitStatus({
          type: "success",
          message: "Thank you! Your message has been sent successfully.",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        setSubmitStatus({
          type: "error",
          message:
            response.data.message ||
            "Failed to send message. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus({
        type: "error",
        message:
          "An error occurred while sending your message. Please try again.",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="contact-us-page">
      <SEO
        title="Contact Us - Grad4Grad"
        description="Get in touch with Grad4Grad for personalized study abroad guidance, university admissions support, and expert advice for international students. We're here to help you achieve your educational dreams."
        keywords="contact grad4grad, study abroad help, university admissions support, international student guidance, educational consulting"
        image="https://grad4grad.com/contact-hero.jpg"
        url="https://grad4grad.com/contact"
        type="website"
      />
      {/* Hero Section */}
      <div
        className="contact-hero"
        style={{
          background: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${contactHero}) no-repeat center center`,
          backgroundSize: "cover",
        }}
      >
        <div className="contact-hero-content">
          <h1>Get In Touch</h1>
          <p>
            We're here to answer any questions you have about studying abroad
          </p>
        </div>
      </div>
      {/* Contact Information Cards */}
      <div className="contact-section contact-info-section">
        <div className="container">
          <div className="contact-cards">
            <div className="contact-card">
              <div className="contact-icon email-icon"></div>
              <h3>Email Us</h3>
              <p>contact@grad4grad.com</p>
            </div>
          </div>
        </div>
      </div>
      {/* Contact Form and Map Section */}
      <div className="contact-section contact-form-section">
        <div className="container">
          <div className="contact-form-container">
            <div className="contact-form-wrapper">
              <h2>Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">Your Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Your Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Your Phone Number (Optional)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Include country code (e.g., +1 for US)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Your Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  disabled={submitLoading}
                >
                  {submitLoading ? "Sending..." : "Send Message"}
                </button>

                {submitStatus && (
                  <div className={`form-status ${submitStatus.type}`}>
                    {submitStatus.message}
                  </div>
                )}
              </form>
            </div>

            {/* <div className="contact-map">
              <div className="map-placeholder">
                <div className="map-pin"></div>
                <h3>Our Location</h3>
                <p>1234 University Road, Suite 500</p>
                <p>New York, NY 10001, USA</p>
              </div>
            </div> */}
          </div>
        </div>
      </div>
      {/* FAQ Section */}
      <div className="contact-section faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Is the counseling service really free?</h3>
              <p>
                Yes! We offer completely free one-on-one counseling sessions to
                all students. Our expert counselors will help you with
                university selection, course guidance, application process, and
                more - all at no cost. We believe in making quality education
                guidance accessible to everyone.
              </p>
            </div>

            <div className="faq-item">
              <h3>What can I expect from a free counseling session?</h3>
              <p>
                During your free counseling session, you'll receive personalized
                guidance on: • University and course selection • Application
                process and requirements • Document preparation and review •
                Scholarship opportunities • Visa guidance • Career prospects Our
                counselors will help you create a customized roadmap for your
                study abroad journey.
              </p>
            </div>

            <div className="faq-item">
              <h3>Can I get help with my application process?</h3>
              <p>
                Absolutely! Our free counseling sessions include comprehensive
                application support. Our counselors will guide you through the
                entire application process, including: • Document preparation
                and review • Statement of Purpose (SOP) guidance • Letter of
                Recommendation (LOR) assistance • Application form review •
                Deadline management • Interview preparation
              </p>
            </div>

            <div className="faq-item">
              <h3>How do I book a free counseling session?</h3>
              <p>
                Booking a free counseling session is easy! Simply click the
                "Book Free Counseling" button on our website or contact us
                through any of our channels. Our team will schedule a session at
                your convenience. No hidden charges or commitments required.
              </p>
            </div>

            <div className="faq-item">
              <h3>Who are the counselors?</h3>
              <p>
                Our counseling team consists of experienced education
                consultants who have successfully guided thousands of students.
                They have extensive knowledge of international education systems
                and stay updated with the latest admission trends and
                requirements. Many of our counselors are former international
                students themselves.
              </p>
            </div>

            <div className="faq-item">
              <h3>What makes Grad4Grad different?</h3>
              <p>
                Grad4Grad stands out by offering completely free, personalized
                counseling services. Unlike other platforms that charge for
                guidance, we believe in making quality education support
                accessible to all. Our counselors provide comprehensive
                assistance throughout your study abroad journey, from initial
                planning to successful admission.
              </p>
            </div>
          </div>
          <div className="faq-cta">
            <h3>Ready to Start Your Journey?</h3>
            <p>
              Book your free counseling session today and take the first step
              towards your dream education!
            </p>
            <a
              href="https://shorturl.at/uvKtH"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button"
            >
              Book Free Counseling Session
            </a>
          </div>
        </div>
      </div>
      {/* Social Media Section */}
      <div className="contact-section social-section">
        <div className="container">
          <h2>Connect With Us</h2>
          <p>
            Stay updated with the latest news, resources, and tips on our social
            media channels
          </p>
          <div className="social-icons">
            <a
              href="https://www.facebook.com/vaibhavtyagi2000/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon facebook"
            ></a>
            <a
              href="https://x.com/vaibhavtyagi123"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon twitter"
            ></a>
            <a
              href="https://www.instagram.com/vaibhavtyagi2024/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon instagram"
            ></a>
            <a
              href="https://www.linkedin.com/in/vaibhavtyagi2024/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon linkedin"
            ></a>
            <a
              href="https://www.youtube.com/@vaibhavtyagi2024"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon youtube"
            ></a>
          </div>
        </div>
      </div>
      <BackToTop />
    </div>
  );
};

export default ContactUs;
